import React, { Component } from 'react';
import { query, repository, subscribe } from '@trlogic/client-js';
import { RootState } from '../../../../redux/store';
import { AiModelState } from '../../../../redux/reducers/AiModelReducer';
import { changeAiModel, changeAiModelIsUpdate, clearAiModel } from '../../../../redux/actions/AiModelActions';
import { enterComponent, leaveComponent } from '../../../../redux/actions/ComponentActions';
import { clearPage } from '../../../../redux/actions/PageActions';
import { connect, ConnectedProps } from 'react-redux';
import QueryBuilder = query.QueryBuilder;
import CollectionNames from '../../../../constant/CollectionNames';
import eq = query.eq;
import QueryableRepository = repository.QueryableRepository;
import { InstanceContainer } from '../../../../config/binding/InstanceBinding';
import Injectables from '../../../../constant/Injectables';
import CustomLoading from '../../../custom/CustomLoading';
import and = query.and;
import { ChartJsDataModel, ChartJsDatasetModel } from '../../../../../../server/src/lib/domain/chart/ChartDomain';
import MLOpsResultChartComponent from './MLOpsResultChartComponent';
import { MLOpsMetricResult, MLOpsMetricResultType } from 'domain/mlops/MLOpsDomains';
import { Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend, ChartData } from 'chart.js';
import MLOpsFutureImportanceTable from './MLOpsFutureImportanceTable';
import MLOpsResultsTable from './MLOpsResultsTable';
import MlOpsMetricResultsTable from './MLOpsMetricResultsTable';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend);

class MlOpsResultComponent extends Component<Props, State> {
  public state: State = new State();

  private mlopsResultSubscription: subscribe.Subscription | undefined;
  private confisionMatrixSubscription: subscribe.Subscription | undefined;
  private computation: subscribe.Computation | undefined;

  private readonly repository: QueryableRepository = InstanceContainer.get(Injectables.REPOSITORY);

  public render() {
    const { metrics, loading, confusionMatrixData, threshold } = this.state;
    const {
      aiModel: { isUpdate },
    } = this.props;

    if (!isUpdate) {
      return null;
    }

    if (loading) {
      return <CustomLoading />;
    }
    return (
      <div className={'flex flex-col gap-5 w-full h-full p-5 !max-h-[calc(100vh-17rem)] scrollable-editor-container overflow-y-scroll'}>
        <MLOpsResultChartComponent
          metrics={metrics}
          confusionMatrixData={confusionMatrixData}
          isLoading={loading}
          onChangeThreshold={this.onChangeThreshold}
          thresholdLoading={this.state.thresholdLoading}
        />
        <MLOpsFutureImportanceTable />
        <MlOpsMetricResultsTable results={this.state.results} />
        <MLOpsResultsTable />
      </div>
    );
  }

  public componentDidMount() {
    this.startTracker();
  }

  public componentWillUnmount() {
    this.stopTracker();
  }

  private startTracker = () => {
    const {
      aiModel: { aiModel, isUpdate },
    } = this.props;

    if (!isUpdate) {
      this.setState({ loading: false, thresholdLoading: false });
      return;
    }
    const query = QueryBuilder.select()
      .from(CollectionNames.AI_METRICS)
      .where(and(eq('name', aiModel.name), eq('version', aiModel.version)))
      .orderBy({
        field: 'threshold',
        operator: 'ASC',
      })
      .build();
    const matrixQuery = QueryBuilder.select()
      .from(CollectionNames.AI_METRICS)
      .where(and(eq('name', aiModel.name), eq('version', aiModel.version), eq('threshold', this.state.threshold)))
      .orderBy({
        field: 'threshold',
        operator: 'ASC',
      })
      .build();
    this.mlopsResultSubscription = subscribe.Formica.subscribe(CollectionNames.AI_METRICS, query);
    this.confisionMatrixSubscription = subscribe.Formica.subscribe(CollectionNames.AI_METRICS, matrixQuery);
    this.computation = subscribe.Tracker.autorun(this.tracker, [this.mlopsResultSubscription, this.confisionMatrixSubscription]);
  };

  private stopTracker = () => {
    this.mlopsResultSubscription?.ready();
    this.confisionMatrixSubscription?.ready();
    this.computation?.stop();
  };

  private resubscribeTracker = async () => {
    if (this.computation == undefined) {
      this.startTracker();
    } else {
      this.computation.onStop(this.startTracker);
      await this.stopTracker();
    }
  };

  private tracker = () => {
    if (this.mlopsResultSubscription?.ready() && this.confisionMatrixSubscription?.ready()) {
      this.buildData();
    }
  };

  private buildData = async () => {
    const {
      aiModel: { aiModel },
    } = this.props;
    const query = QueryBuilder.select()
      .from(CollectionNames.AI_METRICS)
      .where(and(eq('name', aiModel.name), eq('version', aiModel.version)))
      .orderBy({
        field: 'threshold',
        operator: 'ASC',
      })
      .build();
    const results: MLOpsMetricResult[] = (await this.repository.findMany(query)) as MLOpsMetricResult[];
    await this.processMetrics(results);

    const matrixQuery = QueryBuilder.select()
      .from(CollectionNames.AI_METRICS)
      .where(and(eq('name', aiModel.name), eq('version', aiModel.version), eq('threshold', this.state.threshold)))
      .orderBy({
        field: 'threshold',
        operator: 'ASC',
      })
      .build();
    const matrixResult: MLOpsMetricResult = (await this.repository.find(matrixQuery)) as MLOpsMetricResult;
    if (matrixResult != null) {
      this.setState({
        thresholdLoading: false,
        confusionMatrixData: {
          data: [
            [matrixResult.tnr, matrixResult.fpr],
            [matrixResult.fnr, matrixResult.tpr],
          ],
          labels: ['True Negative', 'False Positive', 'False Negative', 'True Positive'],
        },
      });
    }

    this.setState({ matrixResult, results, loading: false });
  };

  async processMetrics(metrics: MLOpsMetricResult[]): Promise<Map<string, ChartData<'line'>>> {
    const metricsMap = new Map<string, ChartData<'line'>>();

    const fprs = metrics.map((metric) => metric.fpr);
    const tprs = metrics.map((metric) => metric.tpr);
    const tnrs = metrics.map((metric) => metric.tnr);
    const fnrs = metrics.map((metric) => metric.fnr);
    const mccValues = metrics.map((metric) => metric.mcc);
    const balancedAccuracies = metrics.map((metric) => metric.balancedAccuracy);
    const fBetaScores = metrics.map((metric) => metric.fBetaScore);
    const gMeans = metrics.map((metric) => metric.gMean);
    const precisions = metrics.map((metric) => metric.precision);
    const recalls = metrics.map((metric) => metric.tpr);
    const f1s = metrics.map((metric) => metric.f1score);
    const thresholds = metrics.map((metric) => metric.threshold);
    // Update confusion matrix data
    // ROC Curve

    const calculateAUC = () => {
      let auc = 0;
      for (let i = 1; i < fprs.length; i++) {
        auc += ((fprs[i] - fprs[i - 1]) * (tprs[i] + tprs[i - 1])) / 2;
      }
      return auc.toFixed(2);
    };

    const curveData = {
      labels: fprs,
      datasets: [
        {
          label: `ROC Curve (AUC = ${calculateAUC()})`,
          data: fprs.map((x, i) => ({ x: x, y: tprs[i] })),
          borderColor: 'rgb(59, 130, 246)',
          backgroundColor: 'rgba(59, 130, 246, 0.5)',
          borderWidth: 2,
          tension: 0.4,
        },
        {
          label: 'Baseline',
          data: [
            { x: 0, y: 0 },
            { x: 1, y: 1 },
          ],
          borderColor: 'rgb(156, 163, 175)',
          backgroundColor: 'rgba(156, 163, 175, 0.5)',
          borderWidth: 2,
          borderDash: [5, 5],
        },
      ],
    };

    metricsMap.set(MLOpsMetricResultType.ROC_CURVE, curveData);

    // FPR and FNR vs Threshold
    metricsMap.set(
      MLOpsMetricResultType.FPR_FNR_THRESHOLD,
      this.generateChartJsData(
        thresholds.map((t) => t.toFixed(2)),
        [this.generateChartJsDataset(fprs, 'False Positive Rate', '#D2332F', '#D2332F'), this.generateChartJsDataset(fnrs, 'False Negative Rate')],
      ),
    );

    // TPR/TNR vs Threshold
    metricsMap.set(
      MLOpsMetricResultType.TPR_TNR_THRESHOLD,
      this.generateChartJsData(
        thresholds.map((t) => t.toFixed(2)),
        [this.generateChartJsDataset(tprs, 'True Positive Rate', '#027A48', '#027A48'), this.generateChartJsDataset(tnrs, 'True Negative Rate')],
      ),
    );

    // Precision-Recall Curve
    metricsMap.set(
      MLOpsMetricResultType.PRECISION_RECALL,
      this.generateChartJsData(
        recalls.map((r) => r.toFixed(2)),
        [this.generateChartJsDataset(precisions, 'Precision-Recall Curve', '#728EFF', '#728EFF')],
      ),
    );

    // MCC vs Threshold
    metricsMap.set(
      MLOpsMetricResultType.MCC_THRESHOLD,
      this.generateChartJsData(
        thresholds.map((t) => t.toFixed(2)),
        [this.generateChartJsDataset(mccValues, 'Matthews Correlation Coefficient', '#284564', '#284564')],
      ),
    );

    // G-Mean vs Threshold
    metricsMap.set(
      MLOpsMetricResultType.GMEAN_THRESHOLD,
      this.generateChartJsData(
        thresholds.map((t) => t.toFixed(2)),
        [this.generateChartJsDataset(gMeans, 'G Mean', '#D2332F', '#D2332F')],
      ),
    );

    // F Beta Score vs Threshold
    metricsMap.set(
      MLOpsMetricResultType.FBETA_THRESHOLD,
      this.generateChartJsData(
        thresholds.map((t) => t.toFixed(2)),
        [this.generateChartJsDataset(fBetaScores, 'F Beta Score', '#D2332F', '#D2332F')],
      ),
    );

    // Balanced Accuracy vs Threshold
    metricsMap.set(
      MLOpsMetricResultType.BALANCED_ACCURACY_THRESHOLD,
      this.generateChartJsData(
        thresholds.map((t) => t.toFixed(2)),
        [this.generateChartJsDataset(balancedAccuracies, 'Balanced Accuracy', '#D2332F', '#D2332F')],
      ),
    );

    // F1 Score vs Threshold
    metricsMap.set(
      MLOpsMetricResultType.F1_THRESHOLD,
      this.generateChartJsData(
        thresholds.map((t) => t.toFixed(2)),
        [this.generateChartJsDataset(f1s, 'F1 Score', '#027A48', '#027A48')],
      ),
    );

    this.setState({ metrics: metricsMap });
    return metricsMap as Map<string, ChartData<'line'>>;
  }

  private generateChartJsData = (labels: string[], datasets: ChartJsDatasetModel[]): ChartJsDataModel => {
    return {
      labels: labels,
      datasets: datasets,
    };
  };

  private generateChartJsDataset = (data: number[], label: string, color: string = '#728EFF', borderColor: string = '#728EFF'): ChartJsDatasetModel => {
    return {
      label: label,
      data: data,
      backgroundColor: color,
      borderColor: borderColor,
      borderWidth: 1,
    };
  };

  private onChangeThreshold = (threshold: number) => {
    this.setState({ threshold, thresholdLoading: true }, () => this.resubscribeTracker());
  };
}

class State {
  loading: boolean = true;

  results: MLOpsMetricResult[] = [];
  metrics: Map<string, ChartData<'line'>> = new Map<string, ChartData<'line'>>();

  confusionMatrixData: { data: number[][]; labels: string[] } | null = null;
  matrixResult: MLOpsMetricResult | undefined = undefined;
  threshold: number = 1;
  thresholdLoading: boolean = true;
}

interface Props extends ReduxProps {}

const mapStateToProps = (state: RootState) => ({
  aiModel: state.aiModel as AiModelState,
  isUpdate: state.aiModel.isUpdate,
  lastCategory: state.page.lastCategory,
});
const mapDispatchToProps = {
  changeAiModelIsUpdate,
  changeAiModel,
  clearAiModel,
  enterComponent,
  leaveComponent,
  clearPage,
};
const connector = connect(mapStateToProps, mapDispatchToProps);
type ReduxProps = ConnectedProps<typeof connector>;

export default connector(MlOpsResultComponent);
